"use client";

import { useId } from "react";

import { cn } from "@/lib/utils";
import {
  CAIXA_DO_SIMBOLO,
  CONTORNO_DAS_LETRAS,
  EXTRUSAO,
  FACETAS,
  LETRAS,
  PAREDES,
  PREENCHIMENTO,
  RESPIRO,
  TRACO_PLANO,
  TRACO_RELEVO,
} from "./desenhoDaMarca";

const PASSOS_DA_EXTRUSAO = 12;
const FORA = { x: -4000, y: -4000, width: 10000, height: 10000 };

/**
 * O símbolo da marca AC3D: o A e o C em treliça, com o "3D" chanfrado.
 *
 * Desenhado em SVG (nítido em qualquer tamanho, sem baixar imagem) a partir
 * de scripts/marca/desenho.py. A cor vem do tema (`--marca`, `--marca-lado`):
 * petróleo no claro, petróleo clareado no escuro.
 *
 * - plano (padrão): para o menu e tamanhos até ~200 px; traço mais encorpado.
 * - `relevo`: a assinatura em tamanho grande (login, telas públicas), com
 *   extrusão, brilho nos tubos e chanfro nas letras, como uma peça impressa.
 *
 * O tamanho vem do `className` (ex.: "h-9 w-auto").
 */
export function SimboloAC3D({
  relevo = false,
  className,
  rotulo = "AC3D Studio",
}: {
  relevo?: boolean;
  className?: string;
  /** Texto para leitores de tela. Vazio quando o nome já está escrito ao lado. */
  rotulo?: string;
}) {
  const id = useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const recorte = `${id}-recorte`;
  const corpo = `${id}-corpo`;
  const [parede, preenchimento] = relevo ? TRACO_RELEVO : TRACO_PLANO;

  return (
    <svg
      viewBox={CAIXA_DO_SIMBOLO}
      className={cn("text-marca", className)}
      role={rotulo ? "img" : undefined}
      aria-label={rotulo || undefined}
      aria-hidden={rotulo ? undefined : true}
    >
      <defs>
        {/* a treliça passa por trás do "3D", com uma folga em volta das letras */}
        <mask id={recorte} maskUnits="userSpaceOnUse" {...FORA}>
          <rect {...FORA} fill="#fff" />
          <path d={CONTORNO_DAS_LETRAS} fill="#000" stroke="#000" strokeWidth={2 * RESPIRO} strokeLinejoin="round" />
        </mask>
        <g id={corpo}>
          <g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" mask={`url(#${recorte})`}>
            <path d={PAREDES} strokeWidth={parede} />
            <path d={PREENCHIMENTO} strokeWidth={preenchimento} />
          </g>
          <path d={LETRAS} fill="currentColor" fillRule="evenodd" />
        </g>
      </defs>

      {relevo && (
        <g className="text-marca-lado">
          {Array.from({ length: PASSOS_DA_EXTRUSAO }, (_, i) => PASSOS_DA_EXTRUSAO - i).map((k) => (
            <use
              key={k}
              href={`#${corpo}`}
              x={(EXTRUSAO[0] * k) / PASSOS_DA_EXTRUSAO}
              y={(EXTRUSAO[1] * k) / PASSOS_DA_EXTRUSAO}
            />
          ))}
        </g>
      )}

      <use href={`#${corpo}`} />

      {relevo && (
        <>
          <g
            fill="none"
            stroke="#fff"
            strokeOpacity={0.28}
            strokeLinecap="round"
            strokeLinejoin="round"
            mask={`url(#${recorte})`}
            transform="translate(-3 -4)"
          >
            <path d={PAREDES} strokeWidth={parede * 0.22} />
            <path d={PREENCHIMENTO} strokeWidth={preenchimento * 0.22} />
          </g>
          {FACETAS.map(([d, tom], i) => (
            <path key={i} d={d} fill={tom > 0 ? "#fff" : "#000"} fillOpacity={Math.abs(tom) * (tom > 0 ? 0.5 : 0.32)} />
          ))}
        </>
      )}
    </svg>
  );
}
